import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Check,
  CheckCircle2,
  ClipboardList,
  Copy,
  Layers,
  Link2,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const INPUT_CLASS = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

const TAB_OPTIONS = [
  { value: "links", label: "Links de inscrição", icon: Link2 },
  { value: "pending", label: "Pendentes", icon: ClipboardList },
];

export default function Registrations() {
  const confirmAction = useDoxaConfirm();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "pending" ? "pending" : "links");
  const [links, setLinks] = useState([]);
  const [pending, setPending] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [title, setTitle] = useState("");
  const [classGroupId, setClassGroupId] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const loadLinks = async () => {
    const { data } = await api.get("/registration-links");
    setLinks(data);
    return data;
  };

  const loadPending = async () => {
    const { data } = await api.get("/registrations/pending");
    setPending(data);
    const visibleIds = new Set(data.map((item) => item.id));
    setSelected((prev) => new Set([...prev].filter((id) => visibleIds.has(id))));
    window.dispatchEvent(new Event("doxa:pending-changed"));
    return data;
  };

  const refreshOperational = async ({ silent = false } = {}) => {
    try {
      await Promise.all([loadLinks(), loadPending()]);
      setLastSyncedAt(Date.now());
      return true;
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
      return false;
    }
  };

  useEffect(() => {
    void refreshOperational();
    api.get("/class-groups").then(({ data }) => {
      setClassGroups(data);
      if (data[0]) setClassGroupId(data[0].id);
    }).catch((err) => toast.error(formatApiError(err)));
  }, []);

  useVisibleRefresh(
    () => refreshOperational({ silent: true }),
    { intervalMs: tab === "pending" ? 15000 : 0 },
  );

  const publicUrl = (token) => `${window.location.origin}/register/${token}`;

  const createLink = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Informe um título para o link.");
    if (!classGroupId) return toast.error("Selecione uma turma.");
    setCreating(true);
    try {
      await api.post("/registration-links", { title, class_group_id: classGroupId });
      toast.success("Link de inscrição criado!");
      setTitle("");
      await refreshOperational({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(publicUrl(token));
    toast.success("Link copiado!");
  };

  const copyWhatsapp = (link) => {
    const msg = `Olá! Faça sua inscrição na Doxa School (${link.class_group_name}): ${publicUrl(link.token)}`;
    navigator.clipboard.writeText(msg);
    toast.success("Mensagem para WhatsApp copiada!");
  };

  const toggleLink = async (link) => {
    try {
      await api.post(`/registration-links/${link.id}/toggle`);
      await refreshOperational({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteLink = (link) => confirmAction({
    title: "Excluir link de inscrição?",
    description: <>O link <strong className="font-semibold text-[#1F1F1F]">{link.title}</strong> deixará de aceitar novas inscrições. As inscrições já enviadas continuam na fila.</>,
    confirmLabel: "Excluir link",
    loadingLabel: "Excluindo…",
    variant: "destructive",
    dataTestId: `confirm-delete-registration-link-${link.id}`,
    onConfirm: async () => {
      await api.delete(`/registration-links/${link.id}`);
      toast.success("Link excluído.");
      await refreshOperational({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const approve = async (student) => {
    try {
      await api.post(`/registrations/${student.id}/approve`);
      toast.success(`${student.full_name} aprovado(a)!`);
      await refreshOperational({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = (student) => confirmAction({
    title: "Recusar inscrição?",
    description: <>A inscrição de <strong className="font-semibold text-[#1F1F1F]">{student.full_name}</strong> será recusada.</>,
    confirmLabel: "Recusar inscrição",
    loadingLabel: "Recusando…",
    variant: "destructive",
    dataTestId: `confirm-reject-registration-${student.id}`,
    onConfirm: async () => {
      await api.post(`/registrations/${student.id}/reject`);
      toast.success("Inscrição recusada.");
      await refreshOperational({ silent: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allSelected = pending.length > 0 && pending.every((student) => selected.has(student.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pending.map((student) => student.id)));
  const selectedList = pending.filter((student) => selected.has(student.id));
  const selClassNames = [...new Set(selectedList.map((student) => student.class_group_name))];

  const bulkApprove = async () => {
    setBulkBusy(true);
    const ids = selectedList.map((student) => student.id);
    const results = await Promise.allSettled(ids.map((id) => api.post(`/registrations/${id}/approve`)));
    const ok = results.filter((result) => result.status === "fulfilled").length;
    const fail = results.length - ok;
    if (fail === 0) toast.success(`${ok} inscrição(ões) aprovada(s) com sucesso.`);
    else if (ok === 0) toast.error(`Nenhuma inscrição pôde ser aprovada (${fail}).`);
    else toast.warning(`${ok} inscrição(ões) aprovada(s). ${fail} não pôde(puderam) ser aprovada(s).`);
    setSelected(new Set());
    setConfirmOpen(false);
    await refreshOperational({ silent: true });
    setBulkBusy(false);
  };

  const activeLinks = links.filter((link) => link.status === "ACTIVE").length;
  const inactiveLinks = links.length - activeLinks;
  const linkedPending = links.reduce((sum, link) => sum + (Number(link.pending_count) || 0), 0);
  const pendingClasses = new Set(pending.map((student) => student.class_group_name).filter(Boolean)).size;
  const classOptions = classGroups.length
    ? classGroups.map((group) => ({ value: group.id, label: group.name }))
    : [{ value: "", label: "Nenhuma turma ativa", disabled: true }];

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="registrations-page">
      <PageHeader
        eyebrow="Gestão"
        title="Inscrições"
        description="Crie portas de entrada para as turmas e acompanhe a fila de novos alunos até a aprovação."
        meta={tab === "pending"
          ? `${pending.length} inscrição(ões) aguardando análise · atualização automática a cada 15 s`
          : `${activeLinks} link(s) ativo(s) · ${links.length} link(s) no total`}
      />

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4" aria-label="Visões de inscrições">
        <div className="inline-flex w-full rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1 sm:w-auto">
          {TAB_OPTIONS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition sm:flex-none ${tab === value ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
              data-testid={value === "links" ? "tab-links" : "tab-pending"}
            >
              <Icon className="h-4 w-4" />
              {label}
              {value === "pending" && pending.length > 0 && (
                <span className="rounded-full bg-[#FDECEC] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#C53030]">{pending.length}</span>
              )}
            </button>
          ))}
        </div>

        {lastSyncedAt && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#8A8378]" data-testid="registrations-auto-refresh">
            <RefreshCw className="h-3 w-3 text-[#F75A1B]" />
            {tab === "pending" ? "Fila sincronizada automaticamente enquanto esta tela estiver visível." : "Dados atualizados ao entrar ou retornar para esta tela."}
          </p>
        )}
      </section>

      {tab === "links" && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              title="Links ativos"
              value={activeLinks}
              hint="Disponíveis para novas inscrições públicas."
              icon={Link2}
              tone="success"
              compact
              testId="registrations-stat-active-links"
            />
            <StatCard
              title="Links inativos"
              value={inactiveLinks}
              hint="Permanecem registrados, mas não recebem novas inscrições."
              icon={Power}
              tone="neutral"
              compact
              testId="registrations-stat-inactive-links"
            />
            <StatCard
              title="Pendências vinculadas"
              value={linkedPending}
              hint="Soma das pendências informadas pelos links atuais."
              icon={ClipboardList}
              tone={linkedPending > 0 ? "warning" : "neutral"}
              compact
              testId="registrations-stat-linked-pending"
            />
          </div>

          <SectionCard
            title="Novo link de inscrição"
            description="Defina um nome fácil de reconhecer e vincule o link à turma que receberá as solicitações."
            icon={Plus}
            testId="registration-new-link-section"
          >
            <form onSubmit={createLink} className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(220px,.8fr)_auto] md:items-end" data-testid="new-link-form">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]" htmlFor="registration-link-title">Título do link</label>
                <input
                  id="registration-link-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Ex.: Inscrições Turma 2026"
                  className={INPUT_CLASS}
                  data-testid="new-link-title"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Turma vinculada</label>
                <DoxaSelect
                  value={classGroupId}
                  onValueChange={setClassGroupId}
                  options={classOptions}
                  disabled={classGroups.length === 0}
                  className="w-full"
                  testId="new-link-class"
                  ariaLabel="Turma vinculada ao novo link de inscrição"
                />
              </div>

              <button
                type="submit"
                disabled={creating || classGroups.length === 0}
                className="doxa-btn-primary inline-flex h-10 items-center justify-center gap-2 px-4 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="new-link-submit"
              >
                <Plus className="h-4 w-4" /> {creating ? "Criando…" : "Criar link"}
              </button>
            </form>
          </SectionCard>

          <SectionCard
            title="Links criados"
            description="Compartilhe o endereço principal e use as ações secundárias para WhatsApp, disponibilidade ou exclusão."
            icon={Link2}
            testId="registration-links-section"
          >
            <div className="grid gap-3 lg:grid-cols-2">
              {links.map((link) => (
                <RegistrationLinkCard
                  key={link.id}
                  link={link}
                  publicUrl={publicUrl}
                  onCopy={() => copyLink(link.token)}
                  onWhatsapp={() => copyWhatsapp(link)}
                  onToggle={() => toggleLink(link)}
                  onDelete={() => deleteLink(link)}
                />
              ))}
              {links.length === 0 && (
                <div className="lg:col-span-2">
                  <EmptyState
                    icon={Link2}
                    title="Nenhum link criado ainda"
                    description="Crie o primeiro link para abrir inscrições em uma turma."
                  />
                </div>
              )}
            </div>
          </SectionCard>
        </>
      )}

      {tab === "pending" && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              title="Aguardando análise"
              value={pending.length}
              hint="Solicitações que ainda precisam de uma decisão da gestão."
              icon={UserPlus}
              tone={pending.length > 0 ? "warning" : "neutral"}
              compact
              testId="registrations-stat-pending"
            />
            <StatCard
              title="Turmas na fila"
              value={pendingClasses}
              hint="Turmas com pelo menos uma inscrição pendente."
              icon={Layers}
              tone="neutral"
              compact
              testId="registrations-stat-pending-classes"
            />
            <StatCard
              title="Selecionadas"
              value={selectedList.length}
              hint="Prontas para aprovação em massa nesta seleção."
              icon={CheckCircle2}
              tone={selectedList.length > 0 ? "orange" : "neutral"}
              compact
              testId="registrations-stat-selected"
            />
          </div>

          <SectionCard
            title="Fila de aprovação"
            description="Revise os dados essenciais, aprove individualmente ou selecione várias inscrições para uma decisão em lote."
            icon={ClipboardList}
            testId="registration-pending-section"
          >
            {pending.length > 0 && (
              <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[#E7DED2] bg-[#FAF7F1] p-3 sm:flex-row sm:items-center" data-testid="pending-bulk-toolbar">
                <label className="inline-flex cursor-pointer select-none items-center gap-2 text-sm font-medium text-[#4A4640]" data-testid="pending-select-all">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-[#F75A1B]"
                  />
                  {allSelected ? "Desmarcar todos" : "Selecionar todos"}
                </label>
                <span className="text-xs font-medium text-[#8A8378]" data-testid="pending-selected-count">{selectedList.length} selecionada(s)</span>
                <button
                  type="button"
                  disabled={selectedList.length === 0 || bulkBusy}
                  onClick={() => setConfirmOpen(true)}
                  className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40 sm:ml-auto"
                  data-testid="approve-selected-btn"
                >
                  <Check className="h-4 w-4" />
                  {bulkBusy ? "Aprovando…" : `Aprovar selecionados${selectedList.length ? ` (${selectedList.length})` : ""}`}
                </button>
              </div>
            )}

            <div className="hidden overflow-hidden rounded-xl border border-[#E7DED2] md:block" data-testid="pending-list">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F1] text-left text-xs uppercase tracking-[0.06em] text-[#8A8378]">
                  <tr>
                    <th className="w-10 px-4 py-3 font-semibold">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="h-4 w-4 accent-[#F75A1B]"
                        data-testid="pending-select-all-header"
                        aria-label="Selecionar todos"
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold">Aluno</th>
                    <th className="px-4 py-3 font-semibold">Célula</th>
                    <th className="px-4 py-3 font-semibold">Turma</th>
                    <th className="px-4 py-3 text-right font-semibold">Decisão</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EBE5DB] bg-white">
                  {pending.map((student) => (
                    <tr key={student.id} className={`transition ${selected.has(student.id) ? "bg-[#FFF9F5]" : "hover:bg-[#FCFAF7]"}`} data-testid={`pending-row-${student.id}`}>
                      <td className="px-4 py-4 align-top">
                        <input
                          type="checkbox"
                          checked={selected.has(student.id)}
                          onChange={() => toggleSel(student.id)}
                          className="h-4 w-4 accent-[#F75A1B]"
                          data-testid={`pending-check-${student.id}`}
                          aria-label={`Selecionar ${student.full_name}`}
                        />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <p className="font-semibold text-[#1F1F1F]">{student.full_name}</p>
                        <p className="mt-1 max-w-[260px] break-all text-xs text-[#6B655E]">{student.email}</p>
                      </td>
                      <td className="px-4 py-4 align-top text-[#6B655E]">{student.cell_group || "—"}</td>
                      <td className="px-4 py-4 align-top">
                        <span className="doxa-badge doxa-badge-neutral">{student.class_group_name}</span>
                      </td>
                      <td className="px-4 py-4 align-top text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => approve(student)}
                          className="mr-2 inline-flex items-center gap-1 rounded-lg bg-[#EAF5EE] px-2.5 py-1.5 text-xs font-semibold text-[#2F7A45] transition hover:bg-[#DDEEE3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F855A]/25"
                          data-testid={`approve-${student.id}`}
                        >
                          <Check className="h-3.5 w-3.5" /> Aprovar
                        </button>
                        <button
                          type="button"
                          onClick={() => reject(student)}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#FDECEC] px-2.5 py-1.5 text-xs font-semibold text-[#C53030] transition hover:bg-[#F9DFDF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C53030]/20"
                          data-testid={`reject-${student.id}`}
                        >
                          <X className="h-3.5 w-3.5" /> Recusar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden" data-testid="pending-cards-mobile">
              {pending.map((student) => (
                <article
                  key={student.id}
                  className={`relative overflow-hidden rounded-2xl border bg-white p-4 transition ${selected.has(student.id) ? "border-[#F75A1B]/40 ring-2 ring-[#F75A1B]/10" : "border-[#E0D9CD]"}`}
                  data-testid={`pending-card-${student.id}`}
                >
                  <span className={`absolute inset-y-0 left-0 w-1 ${selected.has(student.id) ? "bg-[#F75A1B]" : "bg-[#E0D9CD]"}`} aria-hidden="true" />
                  <div className="flex items-start gap-3 pl-1">
                    <input
                      type="checkbox"
                      checked={selected.has(student.id)}
                      onChange={() => toggleSel(student.id)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-[#F75A1B]"
                      data-testid={`pending-check-mobile-${student.id}`}
                      aria-label={`Selecionar ${student.full_name}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-bold text-[#1F1F1F] break-words">{student.full_name}</p>
                      <p className="mt-1 break-all text-xs text-[#6B655E]">{student.email}</p>
                      <div className="mt-3 grid gap-2 rounded-xl bg-[#FAF7F1] p-3 text-xs text-[#6B655E]">
                        <div className="flex items-center justify-between gap-3">
                          <span>Célula</span>
                          <span className="text-right font-semibold text-[#4A4640]">{student.cell_group || "—"}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Turma</span>
                          <span className="text-right font-semibold text-[#4A4640]">{student.class_group_name}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 border-t border-[#EBE5DB] pt-3">
                    <button
                      type="button"
                      onClick={() => approve(student)}
                      className="inline-flex items-center justify-center gap-1 rounded-xl bg-[#EAF5EE] px-2.5 py-2.5 text-xs font-semibold text-[#2F7A45] transition hover:bg-[#DDEEE3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F855A]/25"
                      data-testid={`approve-mobile-${student.id}`}
                    >
                      <Check className="h-3.5 w-3.5" /> Aprovar
                    </button>
                    <button
                      type="button"
                      onClick={() => reject(student)}
                      className="inline-flex items-center justify-center gap-1 rounded-xl bg-[#FDECEC] px-2.5 py-2.5 text-xs font-semibold text-[#C53030] transition hover:bg-[#F9DFDF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C53030]/20"
                      data-testid={`reject-mobile-${student.id}`}
                    >
                      <X className="h-3.5 w-3.5" /> Recusar
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {pending.length === 0 && (
              <EmptyState
                icon={CheckCircle2}
                title="Nenhuma inscrição pendente"
                description="A fila está em dia. Novas solicitações aparecerão aqui automaticamente."
              />
            )}
          </SectionCard>
        </>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => !bulkBusy && setConfirmOpen(false)}>
          <div className="w-full max-w-md rounded-[20px] border border-[#E0D9CD] bg-white p-5 shadow-[0_24px_70px_rgba(45,31,20,.18)] sm:p-6" onClick={(event) => event.stopPropagation()} data-testid="bulk-approve-modal">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#EAF5EE] text-[#2F7A45]">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-bold text-[#1F1F1F]">Aprovar inscrições selecionadas?</h2>
                <p className="mt-1 text-sm leading-relaxed text-[#6B655E]">
                  Você está prestes a aprovar <strong className="text-[#1F1F1F]">{selectedList.length}</strong> inscrição(ões). Depois disso, os alunos poderão acessar o Doxa School conforme o fluxo atual.
                </p>
              </div>
            </div>

            {selClassNames.length === 1 ? (
              <div className="mt-4 rounded-xl border border-[#E7DED2] bg-[#FAF7F1] p-3 text-sm text-[#4A4640]">
                Turma: <span className="font-semibold">{selClassNames[0]}</span>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-[#F0D6A7] bg-[#FFF8EB] p-3 text-sm leading-relaxed text-[#A85A09]">
                As inscrições selecionadas pertencem a turmas diferentes. A aprovação respeitará a turma já vinculada a cada inscrição.
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={bulkBusy}
                className="rounded-xl border border-[#E0D9CD] px-4 py-2.5 text-sm font-semibold text-[#4A4640] transition hover:bg-[#FAF7F1] disabled:opacity-50"
                data-testid="bulk-cancel-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={bulkApprove}
                disabled={bulkBusy}
                className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm"
                data-testid="bulk-confirm-btn"
              >
                <Check className="h-4 w-4" /> {bulkBusy ? "Aprovando…" : "Confirmar aprovação"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RegistrationLinkCard({ link, publicUrl, onCopy, onWhatsapp, onToggle, onDelete }) {
  const active = link.status === "ACTIVE";

  return (
    <article className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5" data-testid={`link-row-${link.id}`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${active ? "bg-[#2F855A]" : "bg-[#A9A39A]"}`} aria-hidden="true" />

      <div className="flex items-start justify-between gap-3 pl-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-bold text-[#1F1F1F] sm:text-lg">{link.title}</h3>
            <span className={`doxa-badge ${active ? "doxa-badge-success" : "doxa-badge-neutral"}`}>{active ? "Ativo" : "Inativo"}</span>
            {link.pending_count > 0 && <span className="doxa-badge doxa-badge-warning">{link.pending_count} pendente(s)</span>}
          </div>
          <p className="mt-1 text-xs font-medium text-[#6B655E]">Turma · {link.class_group_name}</p>
        </div>

        <RegistrationLinkActions link={link} onWhatsapp={onWhatsapp} onToggle={onToggle} onDelete={onDelete} />
      </div>

      <div className="mt-4 rounded-xl border border-[#E7DED2] bg-[#FAF7F1] p-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#A59C90]">Link público</p>
        <p className="mt-1 break-all text-xs leading-relaxed text-[#4A4640]">{publicUrl(link.token)}</p>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#EBE5DB] pt-3">
        <p className="text-[11px] leading-relaxed text-[#8A8378]">{active ? "Pronto para novas inscrições." : "Novas inscrições estão pausadas neste link."}</p>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#FFF0E9] px-3 py-2 text-xs font-semibold text-[#D94A12] transition hover:bg-[#FFE6D9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
          data-testid={`copy-link-${link.id}`}
        >
          <Copy className="h-3.5 w-3.5" /> Copiar link
        </button>
      </div>
    </article>
  );
}

function RegistrationLinkActions({ link, onWhatsapp, onToggle, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#8A8378] transition hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações do link ${link.title}`}
          data-testid={`link-actions-${link.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-56 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações do link</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{link.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onWhatsapp} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`copy-wa-${link.id}`}>
          <MessageCircle className="h-4 w-4" /> Copiar para WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onToggle} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`toggle-link-${link.id}`}>
          <Power className="h-4 w-4" /> {link.status === "ACTIVE" ? "Desativar link" : "Ativar link"}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onDelete} className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#C53030] focus:bg-[#FDECEC] focus:text-[#C53030]" data-testid={`delete-link-${link.id}`}>
          <Trash2 className="h-4 w-4" /> Excluir link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
